import './calendar-filters.scss';
import type { RefObject } from 'react';
import { formatEventDate } from '../../helpers/eventHelpers';
import { formatFriendlyDate } from '../../utils/dateUtils';
import type { CalendarEvent, CalendarFilter, CalendarItem } from '../calendar/calendar.types';

type SearchOption = { kind: 'meeting' | 'event'; item: CalendarItem | CalendarEvent };

type CalendarFiltersProps = {
  areFiltersOpen: boolean;
  onToggleFiltersOpen: () => void;
  activeFilterCount: number;
  calendarFilter: CalendarFilter;
  onFilterChange: (filter: CalendarFilter) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  isSearchFocused: boolean;
  onSearchFocus: () => void;
  searchRef: RefObject<HTMLLabelElement | null>;
  searchOptions: SearchOption[];
  onSelectSearchOption: (item: CalendarItem | CalendarEvent) => void;
};

const CalendarFilters = ({
  areFiltersOpen,
  onToggleFiltersOpen,
  activeFilterCount,
  calendarFilter,
  onFilterChange,
  searchQuery,
  onSearchQueryChange,
  isSearchFocused,
  onSearchFocus,
  searchRef,
  searchOptions,
  onSelectSearchOption,
}: CalendarFiltersProps) => (
  <div className="meeting-calendar__filter-panel" aria-label="Calendar filters">
    <button
      type="button"
      className="meeting-calendar__filter-toggle"
      onClick={onToggleFiltersOpen}
      aria-expanded={areFiltersOpen}
      aria-controls="calendar-filter-controls"
    >
      <span>Filters</span>
      {activeFilterCount > 0 && (
        <span className="meeting-calendar__filter-count" aria-label={`${activeFilterCount} active filters`}>
          {activeFilterCount}
        </span>
      )}
      <span className="meeting-calendar__filter-toggle-icon" aria-hidden="true">
        {areFiltersOpen ? '−' : '+'}
      </span>
    </button>

    {areFiltersOpen && (
      <div className="meeting-calendar__filters" id="calendar-filter-controls">
        <div className="meeting-calendar__filter-group" role="group" aria-label="Filter calendar items">
          {(['all', 'meetings', 'events'] as CalendarFilter[]).map((filter) => (
            <button
              key={filter}
              type="button"
              className={`meeting-calendar__filter-button ${calendarFilter === filter ? 'is-active' : ''}`}
              onClick={() => onFilterChange(filter)}
              aria-pressed={calendarFilter === filter}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        <label className="meeting-calendar__search" ref={searchRef}>
          <span className="meeting-calendar__search-label">Search calendar</span>
          <input
            type="search"
            value={searchQuery}
            onFocus={onSearchFocus}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && searchOptions[0]) {
                event.preventDefault();
                onSelectSearchOption(searchOptions[0].item);
              }
            }}
            placeholder="Search title, location, or details"
            aria-label="Search calendar"
            aria-autocomplete="list"
            aria-controls="calendar-search-options"
          />
          {isSearchFocused && searchOptions.length > 0 && (
            <div className="meeting-calendar__search-options" id="calendar-search-options">
              {searchOptions.map(({ kind, item }) => (
                <button
                  key={`${kind}-${item.id}`}
                  type="button"
                  className="meeting-calendar__search-option"
                  onClick={() => onSelectSearchOption(item)}
                >
                  <span className={`meeting-calendar__search-option-type meeting-calendar__search-option-type--${kind}`}>
                    {kind === 'meeting' ? 'Meeting' : 'Event'}
                  </span>
                  <span className="meeting-calendar__search-option-details">
                    <strong>{item.title}</strong>
                    <small>
                      {kind === 'event' ? formatEventDate(item as CalendarEvent) : formatFriendlyDate((item as CalendarItem).date)} ·{' '}
                      {item.location}
                    </small>
                  </span>
                </button>
              ))}
            </div>
          )}
        </label>
      </div>
    )}
  </div>
);

export default CalendarFilters;
